/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useMemo, useState } from "react";
import { differenceInCalendarDays, startOfMonth, endOfWeek, startOfWeek, endOfMonth, format } from "date-fns";
import styled from "styled-components";
import Text from "../ui/Text";
import { useSchedule } from "../../lib/react-query/useSchedule";
import CalendarDaysSkeleton from "../skeleton/CalendarDaysSkeleton";
import CalendarDay from "./CalendarDay";
import CalendarHeader from "./CalendarHeader";
import CalendarViewToggle from "./CalendarViewToggle";
import { DAYS_OF_WEEK_MON_FIRST, DAYS_OF_WEEK_SUN_FIRST } from "../../constants/constants";
import { useConfig } from "../../lib/react-query/useConfig";
import { localDate } from "../../utils/helpers/date";

const StyledCalendar = styled.div`
  padding: 1rem 1rem 0 1rem;
  border-radius: 1rem;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  box-shadow: 0 4px 4px 0 rgba(0, 32, 51, 0.04), 0 8px 16px 0 rgba(0, 32, 51, 0.04);
`;

const CalendarWeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  place-items: center;
`;

const CalenderWeekDay = styled((props) => <Text {...props} />)`
  text-transform: uppercase;
`;

const CalendarDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const Calendar = ({ date, services, provider, minDate, maxDate, onDayClick, onMonthChange }) => {
  const [isWeekView, setIsWeekView] = useState(false);
  const { data: config } = useConfig();
  const mondayIsFirstDay = config?.monday_is_first_day;
  const isAnyProvider = provider?.id === "any";
  const DAYS_OF_WEEK = mondayIsFirstDay ? DAYS_OF_WEEK_MON_FIRST : DAYS_OF_WEEK_SUN_FIRST;

  const selectedDateLocal = useMemo(() => localDate(date), [date]);

  const calculateWeek = useCallback(() => {
    const start = startOfWeek(selectedDateLocal, { weekStartsOn: mondayIsFirstDay ? 1 : 0 });
    const end = endOfWeek(selectedDateLocal, { weekStartsOn: mondayIsFirstDay ? 1 : 0 });
    return { start, end };
  }, [selectedDateLocal, mondayIsFirstDay]);

  const calculateMonth = useCallback(() => {
    const start = startOfWeek(startOfMonth(selectedDateLocal), { weekStartsOn: mondayIsFirstDay ? 1 : 0 });
    const end = endOfWeek(endOfMonth(selectedDateLocal), { weekStartsOn: mondayIsFirstDay ? 1 : 0 });
    return { start, end };
  }, [selectedDateLocal, mondayIsFirstDay]);

  const { start, end } = useMemo(
    () => (isWeekView ? calculateWeek() : calculateMonth()),
    [isWeekView, calculateWeek, calculateMonth]
  );

  const {
    isLoading,
    isError,
    data: schedule,
  } = useSchedule({
    provider_id: isAnyProvider ? null : provider?.id,
    "service_id[]": services.map((service) => service.id),
    date_from: format(start, "yyyy-MM-dd"),
    date_to: format(end, "yyyy-MM-dd"),
  });

  return (
    <StyledCalendar>
      <CalendarHeader
        date={selectedDateLocal}
        setCurrentDate={onMonthChange}
        isWeekView={isWeekView}
        maxDate={maxDate}
      />

      {isError ? (
        <Text color="error" role="alert">
          Something went wrong
        </Text>
      ) : (
        <>
          <CalendarWeekDays>
            {DAYS_OF_WEEK.map((day) => (
              <CalenderWeekDay key={day} size="tiny">
                {day}
              </CalenderWeekDay>
            ))}
          </CalendarWeekDays>

          <CalendarDays>
            {isLoading && <CalendarDaysSkeleton count={differenceInCalendarDays(end, start) + 1} />}
            {!isLoading &&
              schedule.map((day, index) => (
                <CalendarDay key={index} day={day} date={date} minDate={minDate} maxDate={maxDate} onClick={onDayClick} />
              ))}
          </CalendarDays>
          <CalendarViewToggle isWeekView={isWeekView} setIsWeekView={setIsWeekView} />
        </>
      )}
    </StyledCalendar>
  );
};

export default Calendar;
